import { Link, useLocation } from 'react-router-dom';

import { DataTable, type Column } from '../components/DataTable.tsx';
import { Pagination } from '../components/Pagination.tsx';
import { money, num } from '../lib/format.ts';
import { usePaginatedList } from '../lib/usePaginatedList.ts';

interface SkuRow {
  id: number;
  productId: string;
  name: string;
  brand: string | null;
  category: string | null;
  location: string | null;
  stockQty: number;
  price: number;
  isDataQualityFlag: boolean;
}

export function SkusListPage() {
  const location = useLocation();
  // Snapshot of the list URL — passed as state when navigating into a
  // SKU detail so the detail's "Back" can return to this exact page,
  // search, sort, and filter combination.
  const listUrl = location.pathname + location.search;
  const list = usePaginatedList<SkuRow>({
    endpoint: '/api/dashboard/skus',
    defaultSortColumn: 'name',
    defaultSortDirection: 'ASC',
    filterKeys: ['brand', 'location', 'flagged'],
  });

  const columns: Column<SkuRow>[] = [
    {
      key: 'product_id',
      label: 'SKU',
      sortable: true,
      className: 'mono',
      render: (r) => (
        <Link
          to={`/skus/${encodeURIComponent(r.productId)}`}
          state={{ from: listUrl }}
        >
          {r.productId}
        </Link>
      ),
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (r) => (
        <>
          {r.name}
          {r.isDataQualityFlag && (
            <span className="badge badge-pending" style={{ marginLeft: 8 }}>
              flagged
            </span>
          )}
        </>
      ),
    },
    { key: 'brand', label: 'Brand', sortable: true, render: (r) => r.brand ?? '—' },
    { key: 'location', label: 'Loc', sortable: true, className: 'mono', render: (r) => r.location ?? '—' },
    {
      key: 'stock_qty',
      label: 'Qty',
      sortable: true,
      className: 'num',
      render: (r) => num(r.stockQty),
    },
    {
      key: 'price',
      label: 'Price',
      sortable: true,
      className: 'num',
      render: (r) => money(r.price),
    },
  ];

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">SKUs</h1>
        <span className="main-subtitle">{list.data ? `${list.data.total} total` : ''}</span>
      </header>

      <div className="toolbar">
        <input
          placeholder="Search SKU or name…"
          defaultValue={list.state.search}
          onChange={(e) => list.setSearch(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={list.state.filters.flagged === '1'}
            onChange={(e) => list.setFilter('flagged', e.target.checked ? '1' : '')}
            style={{ width: 'auto', minWidth: 0 }}
          />
          <span title="Generic placeholder SKUs like TILES, MISC, DOCASA — used when no specific product was selected. Surfaced for data-quality cleanup.">
            Generic SKUs only
          </span>
        </label>
      </div>

      {list.error && <div className="error">{(list.error as Error).message}</div>}
      {list.isLoading && !list.data && <div className="loading">Loading…</div>}

      {list.data && (
        <>
          <DataTable
            columns={columns}
            rows={list.data.rows}
            rowKey={(r) => r.id}
            sortColumn={list.state.sortColumn}
            sortDirection={list.state.sortDirection}
            onSortChange={list.toggleSort}
          />
          <Pagination
            page={list.state.page}
            pageSize={list.state.pageSize}
            total={list.data.total}
            onPageChange={list.setPage}
          />
        </>
      )}
    </>
  );
}
